import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useThemeClasses } from '../hooks/useThemeClasses';

const API = process.env.REACT_APP_BACKEND_URL;

export const AdminUserManagement = () => {
  const theme = useThemeClasses();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    role: 'admin'
  });
  
  // Customer promotion state
  const [showPromoteSection, setShowPromoteSection] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [promotionRole, setPromotionRole] = useState('admin');

  const roles = [
    { value: 'admin', label: 'Administrator', description: 'Full access to all admin features', color: 'bg-blue-100 text-blue-800' },
    { value: 'manager', label: 'Manager', description: 'Can manage products, orders, and customers', color: 'bg-purple-100 text-purple-800' },
    { value: 'staff', label: 'Staff', description: 'Limited access to orders and products', color: 'bg-green-100 text-green-800' }
  ];

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/api/admin/admin-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.admins || []);
    } catch (error) {
      console.error('Failed to load admin users:', error);
      toast.error('Failed to load admin users');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Password validation - only for new users or if password is being changed
    if (!editingUser && formData.password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }
    
    if (editingUser && formData.password && formData.password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      
      if (editingUser) {
        // Update existing user
        const updateData = {
          name: formData.name,
          email: formData.email,
          role: formData.role
        };
        
        // Only include password if it's being changed
        if (formData.password) {
          updateData.password = formData.password;
        }
        
        await axios.put(`${API}/api/admin/admin-users/${editingUser.id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Admin user updated successfully!');
      } else {
        // Create new user
        await axios.post(`${API}/api/admin/admin-users`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Admin user created successfully!');
      }
      
      setShowForm(false);
      setEditingUser(null);
      setFormData({ email: '', name: '', password: '', role: 'admin' });
      loadUsers();
    } catch (error) {
      console.error('Failed to save admin user:', error);
      toast.error(error.response?.data?.detail || 'Failed to save admin user');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      name: user.name,
      password: '', // Don't pre-fill password
      role: user.role || 'admin'
    });
    setShowForm(true);
  };

  const handleDelete = async (userId, userEmail) => {
    const currentUser = localStorage.getItem('user_email');
    if (userEmail === currentUser) {
      toast.error('You cannot delete your own account');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete admin user: ${userEmail}?`)) return;
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API}/api/admin/admin-users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Admin user deleted successfully!');
      loadUsers();
    } catch (error) {
      console.error('Failed to delete admin user:', error);
      toast.error('Failed to delete admin user');
    }
  };

  const getRoleBadge = (role) => {
    const roleInfo = roles.find(r => r.value === role) || roles[0];
    return (
      <span className={`px-3 py-1 text-xs rounded-lg font-medium ${roleInfo.color}`}>
        {roleInfo.label}
      </span>
    );
  };
  
  const loadCustomers = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/api/admin/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data.customers || []);
    } catch (error) {
      console.error('Failed to load customers:', error);
      toast.error('Failed to load customers');
    }
  };
  
  const handlePromoteCustomer = async () => {
    if (!selectedCustomer) {
      toast.error('Please select a customer to promote');
      return;
    }
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${API}/api/admin/promote-customer`, {
        customer_email: selectedCustomer.email,
        role: promotionRole
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${selectedCustomer.first_name} ${selectedCustomer.last_name} promoted to ${promotionRole}!`);
      setShowPromoteSection(false);
      setSelectedCustomer(null);
      loadUsers();
    } catch (error) {
      console.error('Failed to promote customer:', error);
      toast.error(error.response?.data?.detail || 'Failed to promote customer');
    }
  };

  if (loading) return <div className="p-8">Loading admin users...</div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className={`text-2xl font-bold ${theme.textPrimary}`}>👤 Admin User Management</h2>
          <p className={`text-sm ${theme.textSecondary} mt-1`}>Create and manage admin accounts with different permission levels</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => {
              setShowPromoteSection(!showPromoteSection);
              setShowForm(false);
              if (!showPromoteSection) {
                loadCustomers();
              }
            }}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            {showPromoteSection ? '✕ Cancel' : '⬆️ Promote Customer'}
          </button>
          <button
            onClick={() => {
              setShowForm(!showForm);
              setShowPromoteSection(false);
              setEditingUser(null);
              setFormData({ email: '', name: '', password: '', role: 'admin' });
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {showForm ? '✕ Cancel' : '+ Create Admin User'}
          </button>
        </div>
      </div>
      
      {/* Promote Customer Section */}
      {showPromoteSection && (
        <div className={`${theme.card} p-6 rounded-lg shadow-sm border border-green-200 dark:border-green-800`}>
          <h3 className={`text-lg font-semibold mb-4 ${theme.textPrimary}`}>
            ⬆️ Promote Customer to Admin
          </h3>
          <p className={`text-sm ${theme.textSecondary} mb-4`}>
            Select an existing customer account to promote to an admin user. They will retain their customer account and gain admin access.
          </p>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Select Customer *</label>
              <select
                value={selectedCustomer?.email || ''}
                onChange={(e) => {
                  const customer = customers.find(c => c.email === e.target.value);
                  setSelectedCustomer(customer);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">-- Select a customer --</option>
                {customers.map(customer => (
                  <option key={customer.email} value={customer.email}>
                    {customer.first_name} {customer.last_name} ({customer.email})
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Admin Role *</label>
              <div className="space-y-2">
                {roles.map((role) => (
                  <label key={role.value} className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="promotion-role"
                      value={role.value}
                      checked={promotionRole === role.value}
                      onChange={(e) => setPromotionRole(e.target.value)}
                      className="mr-3"
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${theme.textPrimary}`}>{role.label}</span>
                        <span className={`px-2 py-0.5 text-xs rounded ${role.color}`}>{role.value}</span>
                      </div>
                      <p className={`text-xs ${theme.textSecondary} ml-0`}>{role.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => {
                  setShowPromoteSection(false);
                  setSelectedCustomer(null);
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handlePromoteCustomer}
                disabled={!selectedCustomer}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Promote to {promotionRole.charAt(0).toUpperCase() + promotionRole.slice(1)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Form */}
      {showForm && (
        <div className={`${theme.card} p-6 rounded-lg shadow-sm border`}>
          <h3 className={`text-lg font-semibold mb-4 ${theme.textPrimary}`}>
            {editingUser ? '✏️ Edit Admin User' : '➕ Create New Admin User'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Email Address *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="john@example.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Password {!editingUser && '*'}
                {editingUser && <span className={`text-xs font-normal ${theme.textSecondary}`}> (Leave blank to keep current password)</span>}
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required={!editingUser}
                minLength="8"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={editingUser ? "Leave blank to keep current" : "Minimum 8 characters"}
              />
              <p className={`text-xs ${theme.textSecondary} mt-1`}>
                💡 {editingUser ? 'Only enter a new password if you want to change it' : 'Use a strong password with letters, numbers, and symbols'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-3">Role & Permissions *</label>
              <div className="space-y-3">
                {roles.map((role) => (
                  <label
                    key={role.value}
                    className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      formData.role === role.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="role"
                      value={role.value}
                      checked={formData.role === role.value}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className="mt-1 mr-3"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{role.label}</span>
                        <span className={`px-2 py-0.5 text-xs rounded ${role.color}`}>{role.value}</span>
                      </div>
                      <p className={`text-sm ${theme.textSecondary}`}>{role.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                {editingUser ? '✓ Update Admin User' : '✓ Create Admin User'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingUser(null);
                }}
                className={`px-6 py-2 bg-gray-200 ${theme.textPrimary} rounded-lg hover:bg-gray-300 transition-colors`}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Users Table */}
      <div className={`${theme.card} rounded-lg shadow-sm border overflow-hidden`}>
        <table className={`min-w-full divide-y ${theme.tableBorder}`}>
          <thead className={theme.tableHeader}>
            <tr>
              <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>User</th>
              <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Role</th>
              <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Status</th>
              <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Created</th>
              <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Actions</th>
            </tr>
          </thead>
          <tbody className={theme.tableBorder}>
            {users.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-12 text-center">
                  <div className="text-gray-400">
                    <p className="text-lg mb-2">👤 No admin users found</p>
                    <p className="text-sm">Create your first admin user to get started</p>
                  </div>
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user.id} className={theme.tableRow}>
                  <td className="px-6 py-4">
                    <div>
                      <div className={`font-medium ${theme.textPrimary}`}>{user.name}</div>
                      <div className={`text-sm ${theme.textSecondary}`}>{user.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {getRoleBadge(user.role || 'admin')}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 text-xs rounded-lg font-medium ${theme.isDark ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-800'}`}>
                      ● Active
                    </span>
                  </td>
                  <td className={`px-6 py-4 text-sm ${theme.textSecondary}`}>
                    {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleEdit(user)}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => handleDelete(user.id, user.email)}
                        className="text-red-600 hover:text-red-800 font-medium"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Permissions Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">ℹ️ Role Permissions</h4>
        <div className="text-sm text-blue-800 space-y-1">
          <p><strong>Administrator:</strong> Full access to all features including settings, users, and content management</p>
          <p><strong>Manager:</strong> Can manage products, orders, customers, and view reports</p>
          <p><strong>Staff:</strong> Limited access to view and process orders, manage basic product information</p>
        </div>
      </div>
    </div>
  );
};